'use client';

import useSWR from 'swr';
import { useMemo } from 'react';

import axiosInstance, { endpoints, fetcher } from 'src/lib/axios';

export type AktieutdelningFörslagDto = {
  åretsResultatFöreSkatt: number;
  bolagsskatt: number;
  åretsResultatEfterSkatt: number;
  grundbelopp: number;
  totaltGränsbelopp: number | null;
  föreslagenAktieutdelning: number;
  skattPåUtdelning: number;
  nettoutdelning: number;
  inkomstbasbelopp: number;
  beskattningsår: number;
};

export type MottagareDto = {
  namn: string;
  personnummer: string;
  email: string;
  telefon: string;
};

export type AktieutdelningDto = {
  id?: string;
  mottagare: MottagareDto;
  belopp: number;
  planeratUtbetalningsdatum?: string;
  utbetalningsdatum?: string;
};

export type AktieutdelningFörOrganisationDto = {
  id?: string;
  aktieutdelningar: AktieutdelningDto[];
};

export async function hämtaAktieutdelningar(orgId: string): Promise<AktieutdelningFörOrganisationDto | null> {
  const res = await axiosInstance.get(endpoints.aktieutdelning.hämta(orgId));
  if (res.status === 204) return null;
  return res.data;
}

export async function sparaAktieutdelningar(
  orgId: string,
  dto: AktieutdelningFörOrganisationDto
): Promise<void> {
  await axiosInstance.post(endpoints.aktieutdelning.spara(orgId), dto);
}

export type BanktransaktionKandidatDto = {
  id: string;
  valutaDatum: string;
  referens: string;
  belopp: number;
};

export type UtbetalningsRadDto = {
  aktieägareNamn: string;
  andelProcent: number;
  förväntatBelopp: number;
  status: 'FÖRVÄNTAD' | 'MATCHAD' | 'BOKFÖRD';
  bokförtBelopp: number | null;
  utbetalningsdatum: string | null;
  affärshändelseId: string | null;
  föreslagenBanktransaktion: BanktransaktionKandidatDto | null;
  möjligaMatchningar: BanktransaktionKandidatDto[];
};

export type BeslutDto = {
  beslutsdatum: string;
  belopp: number;
  beskrivning: string;
  källa: 'AUTOMATISK' | 'MANUELL';
  utbetalningar: UtbetalningsRadDto[];
};

export type UtbetalningsÖversiktDto = {
  beslut: BeslutDto[];
  återståendeSkuld: number;
  varningar: string[];
};

export type BokförUtbetalningRequest = {
  aktieägareNamn: string;
  banktransaktionId: string;
  belopp?: number | null;
};

export function useGetUtbetalningsöversikt(
  orgId: string | undefined,
  start: string | undefined,
  slut: string | undefined
) {
  const url =
    orgId && start && slut
      ? `${endpoints.aktieutdelning.utbetalningar(orgId)}?start=${start}&slut=${slut}`
      : null;

  const { data, isLoading, error, mutate } = useSWR<UtbetalningsÖversiktDto>(url, fetcher);

  return useMemo(
    () => ({
      översikt: data ?? null,
      översiktLoading: isLoading,
      översiktError: error,
      översiktMutate: mutate,
    }),
    [data, isLoading, error, mutate]
  );
}

export async function bokförUtbetalning(
  orgId: string,
  request: BokförUtbetalningRequest
): Promise<string> {
  const res = await axiosInstance.post(endpoints.aktieutdelning.bokförUtbetalning(orgId), request);
  return res.data;
}

export async function registreraStämmobeslut(
  orgId: string,
  start: string,
  slut: string
): Promise<void> {
  await axiosInstance.post(
    `${endpoints.aktieutdelning.registreraStämmobeslut(orgId)}?start=${start}&slut=${slut}`
  );
}

export function useGetAktieutdelningFörslag(
  orgId: string | undefined,
  start: string | undefined,
  slut: string | undefined,
  totaltGränsbelopp: number | null
) {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (slut) params.set('slut', slut);
  if (totaltGränsbelopp != null) params.set('totaltGränsbelopp', String(totaltGränsbelopp));

  const url =
    orgId && start && slut
      ? `${endpoints.aktieutdelning.förslag(orgId)}?${params.toString()}`
      : null;

  const { data, isLoading, error, mutate } = useSWR<AktieutdelningFörslagDto>(url, fetcher);

  return useMemo(
    () => ({
      förslag: data ?? null,
      förslagLoading: isLoading,
      förslagError: error,
      förslagMutate: mutate,
    }),
    [data, isLoading, error, mutate]
  );
}
